/* 
   PAGAIDU RISINĀJUMS (TEMPORARY SOLUTION)
   Šobrīd aizkomentējam RichTextEditor (Tiptap), lai nodrošinātu 100% uzticamu 
   izmaiņu fiksēšanu un saglabāšanu lieliem tekstiem. 
   Pie Rich Text redaktora atgriezīsimies vēlāk.
*/

import { useEffect, useRef } from 'react';

interface RichTextEditorProps {
    content: string;
    onChange: (content: string) => void;
    placeholder?: string;
    readOnly?: boolean;
    minHeight?: string;
    maxHeight?: string;
    className?: string;
}

export function RichTextEditor({
    content,
    onChange,
    placeholder = 'Write something...',
    readOnly = false,
    minHeight = '300px', // Base height (~3x usual)
    maxHeight = '70vh',  // Up to 70% of screen height
    className = ''
}: RichTextEditorProps) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea to fit content
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            const newHeight = Math.max(parseInt(minHeight), textareaRef.current.scrollHeight);
            textareaRef.current.style.height = `${newHeight}px`;
        }
    }, [content, minHeight]);

    return (
        <div className={`flex flex-col border border-gray-200 dark:border-white/10 rounded-2xl overflow-hidden bg-white dark:bg-slate-900/40 ${!readOnly ? 'focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-blue-400 shadow-sm' : ''} transition-all ${className}`}>
            <textarea
                ref={textareaRef}
                value={content || ''}
                onChange={(e) => onChange(e.target.value)}
                readOnly={readOnly}
                placeholder={placeholder}
                className={`w-full p-4 bg-transparent text-gray-900 dark:text-gray-100 resize-none focus:outline-none font-primary leading-relaxed ${!readOnly ? 'cursor-text' : ''}`}
                style={{ 
                    minHeight, 
                    maxHeight, 
                    overflowY: 'auto' 
                }}
            />
        </div>
    );
}

/* 
   Aizkomentētā Tiptap loģika nākotnes atsaucei:
   
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from 'tiptap-markdown';
import Placeholder from '@tiptap/extension-placeholder';
import {
    Bold, Italic, List, ListOrdered
} from 'lucide-react';

export function RichTextEditorTiptap({ content, onChange, placeholder, readOnly, minHeight, maxHeight, className }: any) {
    const lastSyncedContent = useRef<string>('');
    const hasReceivedInitialContent = useRef(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                blockquote: false,
                codeBlock: false,
            }),
            Markdown.configure({
                breaks: true,
                html: true,
            }),
            Placeholder.configure({
                placeholder,
            }),
        ],
        editable: !readOnly,
        content: content || '',
        onUpdate: ({ editor }) => {
            const newContent = (editor.storage as any)?.markdown?.getMarkdown() || '';
            if (newContent !== lastSyncedContent.current) {
                lastSyncedContent.current = newContent;
                onChange(newContent);
            }
        },
        onBlur: ({ editor }) => {
            const newContent = (editor.storage as any)?.markdown?.getMarkdown() || '';
            if (newContent !== lastSyncedContent.current) {
                lastSyncedContent.current = newContent;
                onChange(newContent);
            }
        },
    });

    useEffect(() => {
        if (!editor) return;
        const currentContent = (editor.storage as any)?.markdown?.getMarkdown() || '';
        const normalizedPropContent = content || '';
        const normalizedEditorContent = currentContent || '';
        if (normalizedPropContent !== normalizedEditorContent && normalizedPropContent !== lastSyncedContent.current) {
            editor.commands.setContent(normalizedPropContent);
            lastSyncedContent.current = (editor.storage as any)?.markdown?.getMarkdown() || '';
        } else if (!hasReceivedInitialContent.current) {
            lastSyncedContent.current = normalizedEditorContent;
        }
        hasReceivedInitialContent.current = true;
    }, [editor, content]);

    if (!editor) return null;
    return ( <EditorContent editor={editor} /> );
}
*/
